import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import classNames from 'classnames';
import ReactIntersectionObserver from 'react-intersection-observer';
import isNumber from 'lodash/isNumber';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import Icon from '../icon';
import objectFitPolyfill, { isObjectFitSupport } from '../_util/objectFitPolyfill';
import PictureContext, { PictureProvider } from './PictureContext';
import modalPreview from '../modal/preview';
function Picture(props, ref) {
    const { src, downloadUrl, previewUrl, previewTarget, lazy, className, width, height, prefixCls, style, sources, alt, title, block = true, preview = true, objectFit = 'fill', objectPosition = 'center', status: propStatus, border, index, onClick, children, onPreview, ...rest } = props;
    const url = previewUrl || src;
    const pictureRef = useRef({ src: url, downloadUrl });
    const context = useContext(PictureContext);
    const { getProPrefixCls } = useContext(ConfigContext);
    const customPrefixCls = getProPrefixCls('picture', prefixCls);
    const imgRef = useRef(null);
    const [status, setStatus] = useState(propStatus || 'empty');
    const [inView, setInView] = useState(!lazy || !!propStatus);
    const handlePreview = useCallback(() => {
        if (preview && !previewTarget && status === 'loaded' && url) {
            if (context && isNumber(index)) {
                context.preview(index);
            }
            else {
                modalPreview({ list: [{ src: url, downloadUrl }] });
            }
            if (onPreview) {
                onPreview();
            }
        }
    }, [context, index, preview, previewTarget, status, url, downloadUrl, onPreview]);
    const handleClick = useCallback((e) => {
        handlePreview();
        if (onClick) {
            onClick(e);
        }
    }, [handlePreview, onClick]);
    const wrapperStyle = {
        ...style,
    };
    const elementStyle = {
        objectFit,
        objectPosition,
    };
    if (typeof width !== 'undefined') {
        const w = pxToRem(width) || 0;
        elementStyle.width = w;
        wrapperStyle.width = w;
    }
    if (typeof height !== 'undefined') {
        const h = pxToRem(height) || 0;
        elementStyle.height = h;
        wrapperStyle.height = h;
    }
    useEffect(() => {
        if (!propStatus && inView && src) {
            const img = new Image(width, height);
            const onLoad = () => {
                setStatus('loaded');
            };
            const onError = () => {
                setStatus('error');
            };
            img.addEventListener('load', onLoad, false);
            img.addEventListener('error', onError, false);
            img.src = src;
            return () => {
                img.removeEventListener('load', onLoad, false);
                img.removeEventListener('error', onError, false);
            };
        }
    }, [inView, src, propStatus]);
    useEffect(() => {
        if (propStatus) {
            setStatus(propStatus);
        }
    }, [propStatus]);
    useEffect(() => {
        const { current } = imgRef;
        if (current && !isObjectFitSupport() && objectFit && objectPosition) {
            const onResize = () => objectFitPolyfill(current, objectFit, objectPosition);
            onResize();
            window.addEventListener('resize', onResize, false);
            return () => window.removeEventListener('resize', onResize, false);
        }
    }, [imgRef, objectFit, objectPosition]);
    useEffect(() => {
        if (preview && !previewTarget && context && isNumber(index) && url) {
            const { current } = pictureRef;
            current.src = url;
            context.registerPicture(index, current);
            return () => context.unRegisterPicture(index, current);
        }
    }, [index, context, pictureRef, preview, previewTarget, url]);
    useImperativeHandle(ref, () => ({
        preview: handlePreview,
        getImage: () => imgRef.current,
    }), [handlePreview, imgRef]);
    const renderSources = () => {
        if (sources) {
            return sources.map((source, i) => React.createElement("source", Object.assign({ key: String(i) }, source)));
        }
    };
    const renderImg = () => {
        if (children) {
            return children;
        }
        switch (status) {
            case 'loaded': {
                return (React.createElement("img", Object.assign({ ref: imgRef, style: elementStyle, className: `${customPrefixCls}-img`, src: src, alt: alt || title, title: title }, rest)));
            }
            case 'error':
                return (React.createElement("div", { className: `${customPrefixCls}-icon` },
                    React.createElement(Icon, { type: "sentiment_dissatisfied" })));
            case 'empty':
            default:
                return (React.createElement("div", { className: `${customPrefixCls}-icon` },
                    React.createElement(Icon, { type: "photo_size_select_actual" })));
        }
    };
    const classString = classNames(customPrefixCls, {
        [`${customPrefixCls}-border`]: border,
        [`${customPrefixCls}-block`]: block,
        [`${customPrefixCls}-preview`]: preview && status === 'loaded',
    }, className);
    const isPictureSupport = typeof HTMLPictureElement !== 'undefined';
    const Cmp = isPictureSupport ? 'picture' : 'div';
    const img = renderImg();
    const picture = (React.createElement(Cmp, { className: classString, style: wrapperStyle, onClick: handleClick },
        isPictureSupport && renderSources(),
        preview && previewTarget ? (React.createElement("a", { target: previewTarget, href: url }, img)) : img));
    if (lazy && !propStatus && status !== 'loaded') {
        return (React.createElement(ReactIntersectionObserver, { onChange: setInView }, picture));
    }
    return picture;
}
const ForwardPicture = forwardRef(Picture);
ForwardPicture.Provider = PictureProvider;
ForwardPicture.Context = PictureContext;
ForwardPicture.displayName = 'Picture';
export default ForwardPicture;
